'use client'

import {
  ReactNode,
  useSyncExternalStore,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { usePathname } from 'next/navigation'
import { useAuthStore } from '@/stores'
import { useCurrentUser, useValidateSession } from '@/services/auth/hooks'
import { preloadPhoneLibrary } from '@/utils/phone'
import { isProtectedRoute } from '@/config/routes'
import { logger } from '@/lib/logger'

interface HydrationProviderProps {
  children: ReactNode
}

// Subscribe to a dummy store that never changes
const subscribe = () => () => {}

const AUTH_STORAGE_KEY = 'dday-auth-storage'
const VALIDATION_TIMEOUT_MS = 5000
// Background user-data fetch timeout. The API client already has a 15s
// hard timeout per request; this is a safety net on top of that.
const CURRENT_USER_TIMEOUT_MS = 8000

/**
 * Sync Zustand with React Query
 * Handles session validation and keeps auth state in sync
 */
function AuthSync() {
  const {
    isAuthenticated,
    logout,
    setUser,
    restoreSession,
    user: currentUser,
  } = useAuthStore()
  const pathname = usePathname()
  const isValidatingRef = useRef(false)

  // Check if we're on a protected route
  const onProtectedRoute = pathname ? isProtectedRoute(pathname) : false

  // Validate session when on a protected route but localStorage says not authenticated
  const shouldValidateSession = onProtectedRoute && !isAuthenticated

  const {
    data: sessionData,
    isError: isValidationError,
    isLoading: isValidating,
  } = useValidateSession(shouldValidateSession)

  // Track isValidating in ref for timeout callback.
  // Do NOT write isLoading to the auth store here — isLoading is reserved for
  // auth mutations (login/logout/token refresh). Writing it from a background
  // validation fetch is what caused the infinite loading overlay on refresh.
  useEffect(() => {
    isValidatingRef.current = isValidating
  }, [isValidating])

  // Only fetch user data if authenticated (normal flow)
  const {
    data: userData,
    isError,
    error,
    isLoading: isLoadingUser,
  } = useCurrentUser(isAuthenticated)

  // Safety timeout: if the user fetch hangs for too long, log out.
  // This prevents a stuck spinner only in the edge case where the API
  // client's own timeout (15s) doesn't fire (e.g. service worker swallowing
  // the request). Kept intentionally short so users aren't trapped.
  useEffect(() => {
    if (!isAuthenticated || !isLoadingUser) return

    const timeout = setTimeout(() => {
      if (isLoadingUser && isAuthenticated) {
        logger.warn(
          `⚠️ [Auth] User fetch timed out after ${CURRENT_USER_TIMEOUT_MS}ms`
        )
        logout()
      }
    }, CURRENT_USER_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [isAuthenticated, isLoadingUser, logout])

  // Check if auth storage exists and is valid
  const checkAuthStorage = useCallback(() => {
    const storage = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!storage) {
      // Storage was deleted - log out
      if (isAuthenticated) {
        logout()
      }
      return
    }

    // Check if storage has valid user data
    try {
      const parsed = JSON.parse(storage)
      if (!parsed?.state?.user || !parsed?.state?.isAuthenticated) {
        // Storage exists but user is not authenticated - sync state
        if (isAuthenticated) {
          logout()
        }
      }
    } catch {
      // Invalid JSON - storage corrupted, log out
      if (isAuthenticated) {
        logout()
      }
    }
  }, [isAuthenticated, logout])

  // Handle session validation result
  useEffect(() => {
    if (!shouldValidateSession) return

    // Safety timeout: if validation hangs past our deadline, unblock the app.
    // The auth gate in useRequireAuth has its own timeout that will redirect
    // to login — this just ensures the store doesn't stay dirty.
    const timeout = setTimeout(() => {
      if (isValidatingRef.current) {
        logger.warn('⚠️ [Auth] Session validation timed out')
      }
    }, VALIDATION_TIMEOUT_MS)

    if (isValidationError) {
      clearTimeout(timeout)
      return
    }

    if (sessionData && !isValidating) {
      clearTimeout(timeout)

      const hasValidSession = sessionData.authenticated === true
      const validatedUser = sessionData.data?.user
      const accessToken = sessionData.data?.accessToken

      if (hasValidSession && validatedUser && accessToken) {
        restoreSession(validatedUser, accessToken)
      }
      // If not authenticated, useRequireAuth handles the redirect — no store
      // mutation needed here.
    }

    return () => clearTimeout(timeout)
  }, [
    shouldValidateSession,
    sessionData,
    isValidationError,
    restoreSession,
    isValidating,
  ])

  // Handle current user fetch errors and sync (normal authenticated flow)
  useEffect(() => {
    if (isError) {
      const status = (error as { status?: number })?.status
      // If we get a 401 (unauthenticated) or 404 (user not found/deleted), clear auth state
      if (status === 401 || status === 404) {
        logout()
        return
      }
    }

    // Only sync if authenticated and we have fresh data
    if (isAuthenticated && userData) {
      // Preserve isNewUser flag from current state if it exists
      // This is a transient flag set during login that shouldn't be lost when syncing
      const preservedIsNewUser = currentUser?.isNewUser
      setUser({
        ...userData,
        ...(preservedIsNewUser ? { isNewUser: true } : {}),
      })
    }
  }, [
    userData,
    isAuthenticated,
    isError,
    error,
    setUser,
    logout,
    currentUser?.isNewUser,
  ])

  // Preload heavy libraries after hydration (non-blocking)
  useEffect(() => {
    // Preload google-libphonenumber in the background
    // This ensures phone validation works immediately when needed
    preloadPhoneLibrary()
  }, [])

  // Listen for storage changes (cross-tab) and visibility changes (same-tab check)
  useEffect(() => {
    // Handle cross-tab storage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEY) {
        if (event.newValue === null) {
          // Storage was deleted in another tab
          if (isAuthenticated) {
            logout()
          }
        } else {
          // Storage was updated in another tab - check if still valid
          checkAuthStorage()
        }
      }
    }

    // Handle visibility change - check storage when user returns to tab
    // This catches same-tab deletions (e.g., via dev tools)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuthStorage()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, logout, checkAuthStorage])

  return null
}

/**
 * Global hydration provider - prevents FOUC across entire app
 * Waits for Zustand store to hydrate from localStorage before rendering
 */
export function HydrationProvider({ children }: HydrationProviderProps) {
  const hasHydrated = useAuthStore((state) => state._hasHydrated)

  // Use useSyncExternalStore to safely check if we're on client
  // This avoids hydration mismatch warnings
  const isClient = useSyncExternalStore(
    subscribe,
    () => true,
    () => false
  )

  // Don't render until client-side and hydrated
  if (!isClient || !hasHydrated) {
    return null
  }

  return (
    <>
      <AuthSync />
      <div className="animate-fade-in">{children}</div>
    </>
  )
}

/**
 * Non-blocking auth bootstrap for the full app.
 * Keeps session restoration and background preloads working without delaying
 * first paint on public routes.
 */
export function AuthSyncProvider({ children }: HydrationProviderProps) {
  return (
    <>
      <AuthSync />
      {children}
    </>
  )
}
